/**
 * Ce que les deux AIDES du poste disent, à chaque instant de la scène — PUR.
 *
 *   · « À faire maintenant » (`panels/fan/suggestionScript/ScriptSuggestionBar`)
 *     — la barre au-dessus du composeur : le flow « script 1 » au premier
 *     message du jour d'un nouveau fan, la bulle « c'est combien ? » quand il
 *     demande le prix ;
 *   · Francis (`panels/fan/conseil/ConseilDuPoste`) — l'onglet de droite : le
 *     résumé du fan, où il en est, quoi faire, et les gestes prêts (insérer un
 *     message, préparer la vente).
 *
 * Au poste, l'un et l'autre viennent du serveur (règles, puis Jev). Ici ils se
 * DÉRIVENT de l'état de la simulation : aucun appel, aucun modèle. Les
 * libellés sont ceux du poste, mot pour mot (« À faire maintenant »,
 * « Lancer le flow », « Résumer ce fan », « Où il en est », « Quoi faire »,
 * « Insérer », « Préparer la vente · 50 € »).
 *
 * ⚠️ Kayla, 06-10-2026 : Francis est « l'onglet qui t'aide : il résume le fan,
 * propose quoi vendre et prépare la vente ». Il aide VRAIMENT ici aussi — un
 * candidat qui s'en sert fait ce qu'on attend d'un chatteur en shift. Ce qu'on
 * relève, c'est qu'il s'en est servi (`source: "francis"`, `via: "francis"`),
 * jamais qu'il a « triché ».
 */
import type { Etat } from "./moteur";
import { motifIndisponible } from "./moteur";
import {
  MESSAGE_A_50,
  PLANCHER_DE_LA_VIDEO_CENTS,
  VIDEO_A_30,
  VIDEO_A_50,
  fanParId,
  mediaParId,
} from "./scenario";

/** La vente qu'on prépare : un produit vidéo, un prix, LE média délivré. */
export interface VentePreparee {
  offreCle: string;
  prixCents: number;
  mediaId: string;
}

/** La vente d'un accord : le média conclu, son premier produit, le prix. */
export function venteDe(accord: { mediaId: string; prixCents: number }): VentePreparee {
  const media = mediaParId(accord.mediaId);
  return { offreCle: media?.produits[0] ?? "video_solo", prixCents: accord.prixCents, mediaId: accord.mediaId };
}

export type SuggestionAFaire =
  | { genre: "flow"; motif: string; flowCle: string; flowNom: string }
  | { genre: "bulles"; motif: string; bulles: readonly string[] }
  | null;

/** Ce que « À faire maintenant » montre pour ce fan — `null` = la barre se tait. */
export function suggestionAFaire(etat: Etat, fanId: string | null): SuggestionAFaire {
  const fan = fanParId(fanId);
  if (!fan || etat.fini) return null;
  if (fan.nouveau && motifIndisponible(etat, fan.id, "script_1") === null) {
    return {
      genre: "flow",
      flowCle: "script_1",
      flowNom: "script 1",
      motif: "Nouveau fan : son premier message du jour, il n'a pas encore eu « script 1 ».",
    };
  }
  if (fan.id === etat.sceneFan && etat.etape === "proposer" && etat.run?.statut !== "en_cours") {
    // Le vrai message de la prod (« Après le flow », message 0) — la bulle
    // que la barre propose au poste quand le flow a dit « tu continues ? ».
    return { genre: "bulles", motif: "il demande le prix", bulles: [MESSAGE_A_50] };
  }
  return null;
}

export interface ConseilDeFrancis {
  resume: string;
  ouIlEnEst: string;
  quoiFaire: string;
  /** Un message prêt à insérer dans le composeur. */
  message: string | null;
  /** La vente prête à préparer (la Transaction s'ouvre remplie). */
  vente: VentePreparee | null;
}

/** Ce que Francis dit de ce fan — `null` quand aucune conversation n'est ouverte. */
export function conseilDeFrancis(etat: Etat, fanId: string | null): ConseilDeFrancis | null {
  const fan = fanParId(fanId);
  if (!fan) return null;

  const resume = fan.nouveau
    ? `${fan.nom} vient d'arriver. Il n'a encore rien acheté et n'a reçu aucun message d'accueil.`
    : `${fan.nom} est là depuis quelques jours. Il a déjà reçu « script 1 » et n'a rien acheté.`;
  const vente = venteDe({ mediaId: VIDEO_A_50.id, prixCents: VIDEO_A_50.prixCents });
  const prix = `${VIDEO_A_50.prixCents / 100} €`;

  if (fan.id === etat.sceneFan) {
    if (etat.fini) {
      return { resume, ouIlEnEst: "Client — il a payé", quoiFaire: "Cyrano a repris la conversation.", message: null, vente: null };
    }
    switch (etat.etape) {
      case "flow_en_cours":
        return { resume, ouIlEnEst: "Le flow l'accueille", quoiFaire: "Laisse le flow finir. N'écris pas.", message: null, vente: null };
      case "proposer":
        // La stratégie, pas la phrase : la fiche est dans le Vault, et « fait
        // pour lui » se dit avec ses mots (Kayla, 06-10-2026).
        return {
          resume,
          ouIlEnEst: "Il demande le prix",
          quoiFaire: `Propose-lui ta vidéo de 15 min à ${prix}. Dis-lui ce qu'il y a dedans (fiche dans l'onglet Vault) et que tu la fais pour lui, en direct.`,
          message: MESSAGE_A_50,
          vente: null,
        };
      case "negocier":
        return {
          resume,
          ouIlEnEst: "Il trouve ça cher (il demande 30 €)",
          quoiFaire: `Tiens ta valeur : pour la même vidéo, ${PLANCHER_DE_LA_VIDEO_CENTS / 100} € au plus bas. En dessous, propose-lui une autre vidéo, plus petite (« ${VIDEO_A_30.titre} », ${VIDEO_A_30.prixCents / 100} €) — jamais la même.`,
          message: null,
          vente: null,
        };
      case "transaction":
        return {
          resume,
          ouIlEnEst: "Prêt à payer",
          quoiFaire: "Envoie-lui le lien de paiement, au prix que vous avez conclu.",
          message: null,
          vente: etat.accord ? venteDe(etat.accord) : vente,
        };
      case "rendre":
        return { resume, ouIlEnEst: "Client — il a payé", quoiFaire: "Rends la main à Cyrano avec Reprendre.", message: null, vente: null };
      default:
        break;
    }
  }

  if (fan.nouveau && !etat.run) {
    return { resume, ouIlEnEst: "Nouveau — à accueillir", quoiFaire: "Lance le flow « script 1 ».", message: null, vente: null };
  }
  return {
    resume,
    ouIlEnEst: fan.nouveau ? "Nouveau" : "Ancien fan — déjà accueilli",
    quoiFaire: "Rien d'urgent avec lui pour l'instant.",
    message: null,
    vente: null,
  };
}
